import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import snakeize from 'snakeize';
import striptags from 'striptags';
import { urls } from 'app-constants';
import { pluralize } from 'utils';
import { useCachedMedia } from 'context';
import Icon from 'components/common/Icon';
import TruncateText from 'components/common/TruncateText';
import { DeleteOverlay } from 'components/common/inlines';


const Item = ({
  viewMode,
  counter,
  active,
  selectable,
  selected,
  error,
  onClick,
  onDeleteClick,
  prefix,
  id,
  itemId,
  parent,
  shouldDelete,
  layout,
  slug,
  name,
  nameSource,
  description,
  descriptionSource,
  credit,
  creditSource,
  thumbnailMedia: thumbnailMediaId,
  media,
  text,
  overlayColor,
  textColor,
  overlayOpacity,
  structuredContent,
}) => {
  const attachedMedia = useCachedMedia(media);
  const thumbnailMedia = useCachedMedia(thumbnailMediaId);

  const handleDeleteClick = evt => {
    evt.stopPropagation();
    onDeleteClick();
  };

  const typeLabel = {
    'text-fit': '[Text Slide]',
    'text-bleed': '[Text Slide]',
    blocks: '[Blocks Slide]',
  }[layout];

  const nameText = typeLabel || {
    none: '',
    media_name: attachedMedia ? attachedMedia.name : '',
  }[nameSource] || name;

  const descriptionText = {
    none: '',
    media_long_desc: attachedMedia ? attachedMedia.longDescription : '',
    media_short_desc: attachedMedia ? attachedMedia.shortDescription : '',
  }[descriptionSource] || description;

  let thumbnail = (
    <div className="title-builder-item-thumbnail mr-4">
      <Icon name="content_copy" />
    </div>
  );

  if (media || thumbnailMediaId) {
    thumbnail = (thumbnailMedia || attachedMedia) ? (
      <div className="title-builder-item-thumbnail mr-4">
        <img
          src={(thumbnailMedia || attachedMedia).thumbnail}
          alt="thumbnail"
          {...viewMode === 'grid' ? {} : { width: 100, height: 60 }}
        />
      </div>
    ) : (
      <div className="title-builder-item-thumbnail mr-4">
        <Icon name="steppers" />
      </div>
    );
  }

  const scValue = JSON.stringify(snakeize(structuredContent || []));
  layout = layout || '';

  const formFields = (
    <>
      {id ? <input type="hidden" name={`${prefix}-id`} value={id} /> : null}
      <input type="hidden" name={`${prefix}-uuid`} value={itemId} />
      {shouldDelete ? <input type="hidden" name={`${prefix}-DELETE`} value="1" /> : null}
      <input type="hidden" name={`${prefix}-parent`} value={parent} />
      <input type="hidden" name={`${prefix}-index`} value={counter} />
      <input type="hidden" name={`${prefix}-layout`} value={layout || ''} />
      <input type="hidden" name={`${prefix}-slug`} value={slug || ''} />
      <input type="hidden" name={`${prefix}-name`} value={name || ''} />
      <input type="hidden" name={`${prefix}-name_source`} value={nameSource || ''} />
      <input type="hidden" name={`${prefix}-description`} value={description || ''} />
      <input type="hidden" name={`${prefix}-description_source`} value={descriptionSource || ''} />
      <input type="hidden" name={`${prefix}-credit`} value={credit || ''} />
      <input type="hidden" name={`${prefix}-credit_source`} value={creditSource || ''} />
      <input type="hidden" name={`${prefix}-text`} value={text || ''} />
      <input type="hidden" name={`${prefix}-overlay_color`} value={overlayColor || ''} />
      <input type="hidden" name={`${prefix}-text_color`} value={textColor || ''} />
      <input type="hidden" name={`${prefix}-overlay_opacity`} value={overlayOpacity || ''} />
      <input type="hidden" name={`${prefix}-structured_content`} value={scValue} />
      {media ? <input type="hidden" name={`${prefix}-media`} value={media} /> : null}
    </>
  );

  const containerClasses = classNames({
    active,
    selectable,
    selected,
    error,
    'title-builder-item': true,
    'to-delete': shouldDelete,
  });

  let metaContent;
  if (layout.startsWith('text-')) {
    metaContent = (
      <div className="meta my-1" style={{ lineHeight: 1.25 }}>
        {!!attachedMedia && (
          <div>Media:&nbsp;<a href={`${urls.mediaBase}${attachedMedia.id}/`} target="_blank" rel="noopener noreferrer">{attachedMedia.id}</a></div>
        )}
        <div style={{ whiteSpace: 'nowrap' }}>
          <TruncateText text={striptags(text)} numChars={25} />
        </div>
      </div>
    );
  } else if (layout === 'blocks') {
    metaContent = (
      <div className="meta my-1" style={{ lineHeight: 1.25 }}>
        <div>{pluralize(structuredContent, 'content block')}</div>
        <div style={{ whiteSpace: 'nowrap' }}>
          <TruncateText text={slug} numChars={25} />
        </div>
      </div>
    );
  } else if (attachedMedia) {
    metaContent = (
      <div className="meta my-1" style={{ lineHeight: 1.25 }}>
        <div>Media:&nbsp;<a href={`${urls.mediaBase}${attachedMedia.id}/`} target="_blank" rel="noopener noreferrer">{attachedMedia.id}</a></div>
        <div style={{ whiteSpace: 'nowrap' }} title={attachedMedia.masterFilename}>
          <TruncateText text={attachedMedia.masterFilename} numChars={25} truncateStart />
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses} onClick={onClick}>
      <div className="index-counter">{counter}</div>

      {selectable && (
        <label className="form-checkbox" style={{ pointerEvents: 'none', paddingRight: 0 }}>
          <input type="checkbox" checked={selected} readOnly />
          <i className="form-icon" />
        </label>
      )}

      {thumbnail}

      <div className="title-builder-item-details">
        <h6 className="mb-0"><TruncateText text={nameText} numChars={45} forceBreak /></h6>
        <div className="slideshow-builder-caption">{striptags(descriptionText)}</div>
        {metaContent}
      </div>

      {formFields}
      {shouldDelete ? <DeleteOverlay onUndoClick={handleDeleteClick} /> : null}

      <span
        className="btn-delete tooltip tooltip-left"
        key={shouldDelete}
        onClick={handleDeleteClick}
        data-tooltip={shouldDelete ? 'Undo Remove' : 'Remove Item'}
      >
        {shouldDelete ? <Icon name="undo" /> : <Icon name="delete" fill />}
      </span>

      <span className="btn-move" title="Drag to sort">
        <Icon name="menu" />
      </span>
    </div>
  );
};


Item.propTypes = {
  viewMode: PropTypes.oneOf(['list', 'grid']),
  counter: PropTypes.number,
  active: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  error: PropTypes.bool,
  onClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  prefix: PropTypes.string,
  id: PropTypes.string,
  itemId: PropTypes.string,
  parent: PropTypes.string,
  shouldDelete: PropTypes.bool,
  layout: PropTypes.string,
  slug: PropTypes.string,
  name: PropTypes.string,
  nameSource: PropTypes.string,
  description: PropTypes.string,
  descriptionSource: PropTypes.string,
  credit: PropTypes.string,
  creditSource: PropTypes.string,
  thumbnailMedia: PropTypes.string,
  media: PropTypes.string,
  text: PropTypes.string,
  overlayColor: PropTypes.string,
  textColor: PropTypes.string,
  overlayOpacity: PropTypes.number,
  structuredContent: PropTypes.arrayOf(PropTypes.object),
};

export default Item;
